import { apiClient } from "@/api/client";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { equipment } from "@/equipment/api";
import { locations } from "@/location/api";

export type SnoozeData = {
  id: number;
  equipment: number | null;
  location: number | null;
  start: string;
  end: string | null;
  reason: string | null;
  created_at: string;
};

export type SnoozeListItem = SnoozeData & {
  equipment_name: string | null;
  location_name: string | null;
};

export type CreateSnoozeInput = {
  end?: string | null;
  reason?: string | null;
};

export const snoozeKeys = createQueryKeys("snooze", {
  equipment: (equipmentId: number) => ({
    queryKey: ["equipment", equipmentId],
    queryFn: async () => {
      const res = await apiClient.get<{ results: SnoozeData[] }>(
        `equipment/${equipmentId}/snooze/`
      );
      const list = Array.isArray(res.data) ? res.data : res.data.results;
      return list[0] ?? null;
    },
  }),
  location: (locationId: number) => ({
    queryKey: ["location", locationId],
    queryFn: async () => {
      const res = await apiClient.get<{ results: SnoozeData[] }>(
        `locations/${locationId}/snooze/`
      );
      const list = Array.isArray(res.data) ? res.data : res.data.results;
      return list[0] ?? null;
    },
  }),
  all: () => ({
    queryKey: ["all"],
    queryFn: async () => {
      const res = await apiClient.get<{ results: SnoozeListItem[] }>("snoozes/");
      return res.data.results;
    },
  }),
});

export const useEquipmentSnooze = (equipmentId: number, enabled = true) =>
  useQuery({
    ...snoozeKeys.equipment(equipmentId),
    enabled,
  });

export const useLocationSnooze = (locationId: number, enabled = true) =>
  useQuery({
    ...snoozeKeys.location(locationId),
    enabled,
  });

export const useAllSnoozes = () =>
  useQuery({
    ...snoozeKeys.all(),
  });

const useInvalidateSnooze = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: snoozeKeys._def }),
      queryClient.invalidateQueries({ queryKey: equipment._def }),
      queryClient.invalidateQueries({ queryKey: locations._def }),
    ]);
};

export const useCreateEquipmentSnooze = () => {
  const invalidate = useInvalidateSnooze();

  return useMutation({
    mutationFn: async ({
      equipmentId,
      ...body
    }: CreateSnoozeInput & { equipmentId: number }) => {
      const res = await apiClient.post<SnoozeData>(
        `equipment/${equipmentId}/snooze/`,
        body
      );
      return res.data;
    },
    onSuccess: invalidate,
  });
};

export const useDeleteEquipmentSnooze = () => {
  const invalidate = useInvalidateSnooze();

  return useMutation({
    mutationFn: async ({
      equipmentId,
      snoozeId,
    }: {
      equipmentId: number;
      snoozeId: number;
    }) => {
      await apiClient.delete(`equipment/${equipmentId}/snooze/${snoozeId}/`);
    },
    onSuccess: invalidate,
  });
};

export const useCreateLocationSnooze = () => {
  const invalidate = useInvalidateSnooze();

  return useMutation({
    mutationFn: async ({
      locationId,
      ...body
    }: CreateSnoozeInput & { locationId: number }) => {
      const res = await apiClient.post<SnoozeData>(
        `locations/${locationId}/snooze/`,
        body
      );
      return res.data;
    },
    onSuccess: invalidate,
  });
};

export const useDeleteLocationSnooze = () => {
  const invalidate = useInvalidateSnooze();

  return useMutation({
    mutationFn: async ({
      locationId,
      snoozeId,
    }: {
      locationId: number;
      snoozeId: number;
    }) => {
      await apiClient.delete(`locations/${locationId}/snooze/${snoozeId}/`);
    },
    onSuccess: invalidate,
  });
};
